import type { RunStage } from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { StatusPill } from './StatusPill';

// Stage ladder header for the live fleet-run view (#59) — shows *where in the
// run* the box currently is, above the raw event timeline. Reads the latest
// `run_stage` event (`lib/fleetStream.ts` `latestRunStage`).
//
// AN ABSENT LADDER IS AN ANSWER, not a blank. Only 1,119 of 7,140 prod runs emit
// `run_stage` at all, so the one element on the page answering "where is this
// run" was missing on 84% of them with nothing saying why — indistinguishable
// from a ladder that failed to render. `stage === null` now states that this run
// reported no stage and points at the timeline, which IS the full record. It
// still paints nothing before the FIRST frame arrives (`pending`), because
// "reported no stage" is a claim only a stream that has delivered something may
// make — the same rule the empty state below it follows.

// Live ladder order — mirrors `RUN_STAGES` (`@developerz/domain`) minus the
// two terminal escapes, which render as a distinct danger/warning pill
// instead of a ladder position (neither is "further along" than `handoff`).
const LADDER = ['preparing', 'surveying', 'working', 'pr_open', 'handoff', 'done'] as const;

export type RunStageProgressProps = {
  readonly stage: RunStage | null;
  /**
   * Has the stream delivered anything yet? While false the component paints
   * nothing: a run with zero rows so far has not "reported no stage", it has
   * reported nothing at all.
   */
  readonly settled: boolean;
};

function stageLabel(stage: RunStage): string {
  const key = `fleet.stage_${stage}`;
  return t.has(key) ? t(key) : stage;
}

export function RunStageProgress(props: RunStageProgressProps): JSX.Element | null {
  return (
    <Show
      when={props.stage}
      keyed
      fallback={
        <Show when={props.settled}>
          <p class="timeline__meta" role="status">
            {t('fleet.stage_unreported')}
          </p>
        </Show>
      }
    >
      {(stage) => (
        <Show
          when={stage !== 'blocked' && stage !== 'failed'}
          fallback={
            <StatusPill tone={stage === 'failed' ? 'danger' : 'warning'}>
              {stageLabel(stage)}
            </StatusPill>
          }
        >
          <ol class="step-grid run-stage-progress" aria-label={t('fleet.stage_progress_aria')}>
            <For each={LADDER}>
              {(rung, i) => (
                <li
                  class="step-grid__step"
                  classList={{
                    'step-grid__step--done': i() < LADDER.indexOf(stage as (typeof LADDER)[number]),
                    'step-grid__step--active': rung === stage,
                  }}
                  aria-current={rung === stage ? 'step' : undefined}
                >
                  {stageLabel(rung)}
                </li>
              )}
            </For>
          </ol>
        </Show>
      )}
    </Show>
  );
}
